import { useState, useRef, useEffect, useCallback } from 'react'
import { useUIStore } from '@/stores'
import { useThread, useSendMessage, useGenerateSummary } from '@/hooks'
import { Send, Loader2, FileText, AlertCircle, Download, Mic, MicOff } from 'lucide-react'
import { formatTime, getModelShortName, getProviderColor } from '@/utils/helpers'
import { useT } from '@/i18n'
import { api } from '@/services/api-client'

// Web Speech API types
declare global {
  interface Window {
    SpeechRecognition: typeof SpeechRecognition
    webkitSpeechRecognition: typeof SpeechRecognition
  }
}

export function ThreadView() {
  const t = useT()
  const { activeThreadId, selectedProvider, selectedModel, setSelectedProvider, setSelectedModel } = useUIStore()
  const { data: threadData, isLoading } = useThread(activeThreadId)
  const sendMessage = useSendMessage(activeThreadId || '')
  const generateSummary = useGenerateSummary(activeThreadId || '')
  const [input, setInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [showExport, setShowExport] = useState(false)
  const [isRecording, setIsRecording] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const recognitionRef = useRef<SpeechRecognition | null>(null)
  const interimRef = useRef<string>('')

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [threadData?.messages])

  const stopRecording = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop()
      recognitionRef.current = null
    }
    setIsRecording(false)
    interimRef.current = ''
  }, [])

  const toggleRecording = useCallback(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognitionAPI) {
      setError(t.micNotSupported)
      return
    }

    if (isRecording) {
      stopRecording()
      return
    }

    const recognition = new SpeechRecognitionAPI()
    recognition.continuous = true
    recognition.interimResults = true
    recognition.lang = navigator.language || 'pl-PL'

    let baseText = ''

    recognition.onstart = () => {
      baseText = input
      interimRef.current = ''
      setIsRecording(true)
    }

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interim = ''
      let final = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript
        if (event.results[i].isFinal) {
          final += transcript + ' '
        } else {
          interim += transcript
        }
      }
      if (final) {
        baseText = (baseText + (baseText ? ' ' : '') + final.trim()).trim() + ' '
        interimRef.current = ''
      } else {
        interimRef.current = interim
      }
      setInput(baseText + interimRef.current)
    }

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error !== 'aborted' && event.error !== 'no-speech') {
        setError(`Voice input error: ${event.error}`)
      }
      setIsRecording(false)
    }

    recognition.onend = () => {
      setIsRecording(false)
      recognitionRef.current = null
    }

    recognitionRef.current = recognition
    recognition.start()
  }, [isRecording, input, stopRecording, t.micNotSupported])

  // Stop recording when component unmounts or thread changes
  useEffect(() => {
    return () => { stopRecording() }
  }, [activeThreadId, stopRecording])

  // When thread loads and has a default provider, apply it if nothing is currently selected
  useEffect(() => {
    if (!threadData) return
    if (selectedProvider === '' && threadData.default_provider) {
      setSelectedProvider(threadData.default_provider)
      if (threadData.default_model) setSelectedModel(threadData.default_model)
    }
  }, [threadData?.id, threadData?.default_provider])

  if (!activeThreadId) return <div className="empty-state"><p>{t.threadSelect}</p></div>
  if (isLoading) return <div className="empty-state"><Loader2 size={24} className="spinner" /> <p>{t.threadLoading}</p></div>
  if (!threadData) return <div className="empty-state"><p>{t.threadNotFound}</p></div>

  const messages = threadData.messages || []

  const friendlyError = (msg: string): string => {
    if (msg.includes('429') || msg.includes('quota') || msg.includes('rate_limit'))
      return 'API quota exceeded. Check your plan/billing or try a different model.'
    if (msg.includes('401') || msg.includes('Unauthorized') || msg.includes('invalid_api_key'))
      return 'Invalid API key. Check your key in API Keys settings.'
    if (msg.includes('403') || msg.includes('Forbidden'))
      return 'Access denied. This model may not be available on your plan.'
    if (msg.includes('timeout') || msg.includes('ETIMEDOUT'))
      return 'Request timed out. Try again or use a different model.'
    if (msg.includes('500') || msg.includes('Internal'))
      return 'AI provider server error. Try again in a moment.'
    if (msg.includes('network') || msg.includes('ECONNREFUSED'))
      return 'Network error. Check your connection.'
    return msg
  }

  const handleSend = async () => {
    if (!input.trim() || sendMessage.isPending) return
    setError(null)
    const content = input.trim()
    setInput('')

    const isNotesMode = !selectedProvider
    sendMessage.mutate(
      { content, provider: selectedProvider, model: selectedModel },
      {
        onError: (err: Error) => setError(friendlyError(err.message)),
        onSuccess: (data) => {
          if (data.error && !isNotesMode) setError(friendlyError(data.error))
        },
      }
    )
  }

  const handleGenerateSummary = () => {
    generateSummary.mutate({ provider: selectedProvider, model: selectedModel })
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className="thread-view">
      <div className="thread-header">
        <div className="thread-header-left">
          <h1>{threadData.title}</h1>
          {threadData.description && <p className="thread-description">{threadData.description}</p>}
        </div>
        <div className="thread-header-right">
          <span className="provider-badge" style={{ borderColor: getProviderColor(threadData.default_provider) }}>
            {getModelShortName(threadData.default_model)}
          </span>
          <span className="message-count">{messages.length} {t.messages}</span>
          <div className="thread-export-wrap">
            <button className="thread-export-btn" onClick={() => setShowExport(!showExport)} title="Export">
              <Download size={14} />
            </button>
            {showExport && (
              <div className="thread-export-dropdown">
                {(['openai', 'anthropic', 'google', 'synapsios', 'markdown'] as const).map(fmt => (
                  <button key={fmt} onClick={async () => {
                    setShowExport(false)
                    if (fmt === 'markdown') {
                      const url = api.exportThreadMarkdown(activeThreadId!)
                      window.open(url, '_blank')
                      return
                    }
                    try {
                      const data = await api.exportThread(activeThreadId!, fmt)
                      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
                      const a = document.createElement('a')
                      a.href = URL.createObjectURL(blob)
                      a.download = `${threadData.title.replace(/[^a-zA-Z0-9]/g, '_')}_${fmt}.json`
                      a.click()
                      URL.revokeObjectURL(a.href)
                    } catch { /* */ }
                  }}>
                    {fmt === 'openai' ? 'OpenAI' : fmt === 'anthropic' ? 'Anthropic' : fmt === 'google' ? 'Google Gemini' : fmt === 'synapsios' ? 'Synapsios (full)' : 'Markdown'}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="thread-messages">
        {messages.length === 0 && (
          <div className="empty-state small">
            <p>{t.threadNoMessages}</p>
          </div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className={`message message-${msg.role}`}>
            <div className="message-header">
              <span className="message-role">{msg.role === 'user' ? t.you : t.ai}</span>
              {msg.model_used && (
                <span className="message-model" style={{ borderColor: getProviderColor(msg.provider_used || '') }}>
                  {getModelShortName(msg.model_used)}
                </span>
              )}
              {msg.provider_used && <span className="message-provider">{msg.provider_used}</span>}
              <span className="message-time">{formatTime(msg.created_at)}</span>
            </div>
            <div className="message-content">
              {msg.content.split('\n').map((line, i) => {
                if (line.startsWith('```')) {
                  return <code key={i} className="message-code-marker">{line}</code>
                }
                if (line.startsWith('**') && line.endsWith('**')) {
                  return <p key={i}><strong>{line.slice(2, -2)}</strong></p>
                }
                return <p key={i}>{line || '\u00A0'}</p>
              })}
            </div>
          </div>
        ))}
        {sendMessage.isPending && selectedProvider && (
          <div className="message message-loading">
            <Loader2 size={16} className="spinner" />
            <span>{t.threadWaiting.replace('{model}', getModelShortName(selectedModel))}</span>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div className="thread-error">
          <AlertCircle size={14} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>{t.dismiss}</button>
        </div>
      )}

      <div className="thread-input">
        <div className="textarea-wrap">
          <textarea
            ref={textareaRef}
            className={`message-input${isRecording ? ' message-input--recording' : ''}`}
            placeholder={selectedProvider ? `${t.threadPlaceholder.replace('{model}', getModelShortName(selectedModel))} (Shift+Enter for newline)` : 'Dodaj notatkę… (Shift+Enter nowa linia)'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={3}
            disabled={sendMessage.isPending}
          />
          <button
            className={`mic-btn${isRecording ? ' mic-btn--active' : ''}`}
            onClick={toggleRecording}
            title={isRecording ? t.micStop : t.micStart}
            disabled={sendMessage.isPending}
          >
            {isRecording ? <MicOff size={15} /> : <Mic size={15} />}
          </button>
        </div>
        <div className="input-actions">
          <div className="input-actions-left">
            <button className="thread-action-btn" onClick={handleGenerateSummary} disabled={generateSummary.isPending || messages.length === 0}>
              <FileText size={14} />
              {generateSummary.isPending ? t.threadGenerating : t.threadSummary}
            </button>
          </div>
          <button className="send-btn" onClick={handleSend} disabled={!input.trim() || sendMessage.isPending}>
            {sendMessage.isPending ? <Loader2 size={16} className="spinner" /> : <Send size={16} />}
            {t.send}
          </button>
        </div>
      </div>
    </div>
  )
}
